import { ArrowUpTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import type { ObjectDefinitionSpec } from 'shared-types';
import { Button } from '../../../components/Button';
import { EarthLoader } from '../../../components/loaders/EarthLoader';
import { Modal } from '../../../components/Modal';
import { tessellateObjectShape } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { T, useT } from '../../../translation/src';
import { ObjectDefinitionAssetKind } from '../../../types/graphqlTypes';
import { GlyphPreview } from '../../objectLibrary/GlyphPreview';
import { addShapes, groupShapes, roundCoord } from '../document/specOps';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { FieldRow, Segmented, SelectField, ToggleRow } from '../panels/fields';
import { useUploadObjectAsset } from '../useUploadObjectAsset';
import { convertImport, type ImportResult } from './importResult';
import { DRAWING_ACCEPT, drawingFormat, readDrawing } from './readDrawing';

const UNITS: ReadonlyArray<{ value: string; label: string; meters: number }> = [
  { value: 'mm', label: 'mm', meters: 0.001 },
  { value: 'cm', label: 'cm', meters: 0.01 },
  { value: 'm', label: 'm', meters: 1 },
  { value: 'in', label: 'in', meters: 0.0254 },
  { value: 'ft', label: 'ft', meters: 0.3048 },
  // SVG user units at CSS 96 dpi.
  { value: 'px', label: 'px (96 dpi)', meters: 0.0254 / 96 },
];

interface Loaded {
  file: File;
  result: ImportResult;
}

/** Everything the file review needs, seeded from what the file says. */
const ImportReview: React.FC<{ loaded: Loaded; spec: ObjectDefinitionSpec; onCancel: () => void; onImport: (next: (spec: ObjectDefinitionSpec) => { spec: ObjectDefinitionSpec; groupId: string | null }) => void }> = ({
  loaded,
  spec,
  onCancel,
  onImport,
}) => {
  const { result, file } = loaded;
  const knownUnit = UNITS.find((u) => Math.abs(u.meters - (result.metersPerUnit ?? -1)) < 1e-12);
  const [layers, setLayers] = useState<ReadonlySet<string>>(() => new Set(result.layers.map((l) => l.name)));
  const [mode, setMode] = useState<'fit' | 'real'>(knownUnit ? 'real' : 'fit');
  const [unit, setUnit] = useState(knownUnit?.value ?? (result.format === 'svg' ? 'px' : 'mm'));
  const [resizeObject, setResizeObject] = useState(true);
  // A blank starting outline gets replaced; a real drawing gets added to.
  const [replace, setReplace] = useState(spec.shapes.length <= 1);

  const meters = UNITS.find((u) => u.value === unit)?.meters ?? 0.001;
  const converted = useMemo(
    () => convertImport(result, layers, mode === 'fit' ? { mode: 'fit', width: spec.defaultSize.width, depth: spec.defaultSize.depth } : { mode: 'real', metersPerUnit: meters }),
    [result, layers, mode, meters, spec.defaultSize.width, spec.defaultSize.depth],
  );
  const preview = useMemo(
    () =>
      converted.shapes.flatMap((s) =>
        tessellateObjectShape(s).flatMap((poly) => poly.points.slice(1).flatMap((p, i) => [poly.points[i][0], poly.points[i][1], 0, p[0], p[1], 0])),
      ),
    [converted],
  );
  const groupName = file.name.replace(/\.[^.]+$/, '').slice(0, 80) || 'import';

  const apply = () =>
    onImport((current) => {
      let next: ObjectDefinitionSpec = replace ? { ...current, shapes: [], groups: [] } : current;
      if (mode === 'real' && resizeObject && converted.size.width > 0.01 && converted.size.depth > 0.01) {
        next = { ...next, defaultSize: { ...next.defaultSize, width: roundCoord(converted.size.width), depth: roundCoord(converted.size.depth) } };
      }
      next = addShapes(next, converted.shapes);
      if (converted.shapes.length < 2) return { spec: next, groupId: null };
      const grouped = groupShapes(next, converted.shapes.map((s) => s.id), groupName);
      return { spec: grouped.spec, groupId: grouped.groupId };
    });

  return (
    <>
      <Modal.Body className="grid grid-cols-[1fr_18rem] gap-5">
        <div className="flex min-h-72 flex-col rounded-xl border border-gray-200 bg-[#f4f5f5] p-4 text-gray-800 dark:border-gray-700 dark:bg-[#262626] dark:text-gray-100">
          <div className="min-h-0 flex-1">{preview.length > 0 ? <GlyphPreview segments={preview} /> : <p className="py-16 text-center text-sm text-gray-500"><T _str="nothing to import on the selected layers" swc /></p>}</div>
          <p className="mt-2 text-center text-xs text-gray-500">
            {converted.shapes.length} <T _str="shapes" /> · {converted.size.width.toFixed(2)} × {converted.size.depth.toFixed(2)} m
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {result.layers.length > 1 && (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">
                <T _str="layers" swc />
              </span>
              <ul className="max-h-40 overflow-y-auto rounded-md border border-gray-200 p-1 dark:border-gray-700">
                {result.layers.map((layer) => (
                  <li key={layer.name}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-gray-100 dark:hover:bg-black">
                      <input
                        type="checkbox"
                        checked={layers.has(layer.name)}
                        onChange={(e) =>
                          setLayers((current) => {
                            const next = new Set(current);
                            if (e.target.checked) next.add(layer.name);
                            else next.delete(layer.name);
                            return next;
                          })
                        }
                      />
                      <span className="flex-1 truncate">{layer.name}</span>
                      <span className="text-xs text-gray-500">{layer.shapeCount}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <FieldRow label={<T _str="size" swc />}>
            <Segmented
              value={mode}
              options={[
                { value: 'fit', label: <T _str="fit the object" swc /> },
                { value: 'real', label: <T _str="real size" swc /> },
              ]}
              onChange={setMode}
            />
          </FieldRow>
          {mode === 'real' && (
            <>
              <FieldRow label={<T _str="drawing unit" swc />} hint={knownUnit ? <T _str="read from the file" swc /> : <T _str="the file does not say; pick the unit it was drawn in" swc />}>
                <SelectField value={unit} options={UNITS.map(({ value, label }) => ({ value, label }))} onChange={setUnit} />
              </FieldRow>
              <ToggleRow label={<T _str="make the object this size" swc />} checked={resizeObject} onChange={setResizeObject} />
            </>
          )}
          <ToggleRow label={<T _str="replace the current drawing" swc />} checked={replace} onChange={setReplace} />
          {result.warnings.length > 0 && (
            <ul className="rounded-md bg-warning-yellow-200/60 p-2 text-xs text-gray-900">
              {result.warnings.map((w) => (
                <li key={w} className="flex gap-1">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  {w}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-end gap-2">
        <Button variant="secondary" size="xs" onClick={onCancel}>
          <T _str="cancel" swc />
        </Button>
        <Button variant="primary" size="xs" onClick={apply} disabled={converted.shapes.length === 0}>
          <T _str="import" swc />
        </Button>
      </Modal.Footer>
    </>
  );
};

/**
 * Import an SVG, DXF or DWG drawing into the object: read in a worker (Rust
 * WebAssembly), review layers and scale, then add it as one group. The file
 * itself is kept as a source asset of the object.
 */
const ImportDialog_: React.FC<{ open: boolean; onClose: () => void; onError: (message: string) => void }> = ({ open, onClose, onError }) => {
  const { store, organisationId, definition } = useObjectEditor();
  const { spec } = useEditorState();
  const upload = useUploadObjectAsset(organisationId, definition.id);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const messages = {
    unsupported: useT('this file type is not supported: use SVG, DXF or DWG', { swc: true }),
    unreadable: useT('the file could not be read', { swc: true }),
    uploadFailed: useT('the drawing was imported, but its source file could not be stored', { swc: true }),
  };

  const close = () => {
    setLoaded(null);
    setReadError(null);
    onClose();
  };

  const onDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (!drawingFormat(file.name)) {
      setReadError(messages.unsupported);
      return;
    }
    setReadError(null);
    setLoading(true);
    try {
      setLoaded({ file, result: await readDrawing(file) });
    } catch (e) {
      setReadError(`${messages.unreadable}${e instanceof Error && e.message ? ` (${e.message})` : ''}`);
    } finally {
      setLoading(false);
    }
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: (files) => void onDrop(files), multiple: false, accept: DRAWING_ACCEPT, noKeyboard: false });

  return (
    <Modal.Container open={open} onClose={close} panelClassName={loaded ? 'max-w-4xl' : 'max-w-lg'}>
      <Modal.Header onClose={close}>
        <T _str="import a drawing" swc />
      </Modal.Header>
      {loaded ? (
        <ImportReview
          loaded={loaded}
          spec={spec}
          onCancel={() => setLoaded(null)}
          onImport={(next) => {
            const { spec: nextSpec, groupId } = next(store.getState().spec);
            store.apply(() => nextSpec, groupId ? { kind: 'group', id: groupId } : { kind: 'none' });
            const { file } = loaded;
            upload(file, file.name, ObjectDefinitionAssetKind.Source_2D, file.name.split('.').pop() ?? 'bin').catch(() => onError(messages.uploadFailed));
            close();
          }}
        />
      ) : (
        <Modal.Body>
          <div
            {...getRootProps()}
            className={classNames(
              'flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors',
              isDragActive ? 'border-french-violet-600 bg-french-violet-600/5' : 'border-gray-300 hover:border-gray-500 dark:border-gray-600',
            )}
          >
            <input {...getInputProps()} />
            {loading ? (
              <EarthLoader />
            ) : (
              <>
                <ArrowUpTrayIcon className="h-10 w-10 text-gray-400" />
                <p className="font-medium">
                  <T _str="drop an SVG, DXF or DWG file here, or click to choose one" swc />
                </p>
                <p className="text-sm text-gray-500">
                  <T _str="lines, polylines, arcs, circles, splines and blocks are imported; text, dimensions and fills are left out" swc />
                </p>
              </>
            )}
          </div>
          {readError && (
            <p role="alert" className="mt-3 rounded-lg bg-error-red-500/10 px-3 py-2 text-sm text-error-red-500">
              {readError}
            </p>
          )}
        </Modal.Body>
      )}
    </Modal.Container>
  );
};

export const ImportDialog = memo(ImportDialog_);
